<template>
  <div
    class="modal fade"
    :class="{ show: isVisible }"
    :style="{ display: isVisible ? 'block' : 'none' }"
    tabindex="-1"
    aria-labelledby="partialPaymentModalLabel"
    aria-modal="true"
    role="dialog"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content" style="background: #111c44">
        <div class="modal-header" style="border-bottom: none">
          <div>
            <h6 class="modal-title mb-0" id="partialPaymentModalLabel">
              {{ $t("partial_payment.title") }}
            </h6>
            <p class="text-xs mb-0" style="opacity: 0.6">
              {{ $t("partial_payment.subtitle") }}
            </p>
          </div>
          <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
        </div>

        <div class="modal-body pt-2">
          <!-- Récapitulatif élève / tranche -->
          <div class="recap mb-4" v-if="student">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <p class="mb-0 text-sm font-weight-bold">
                  {{ student.first_name }} {{ student.last_name }}
                </p>
                <p class="mb-0 text-xs text-uppercase" style="opacity: 0.6">
                  {{ student.registration_number }}
                </p>
              </div>
              <div class="text-end">
                <p class="mb-0 text-xs" style="opacity: 0.6">
                  {{ $t("partial_payment.remaining") }}
                </p>
                <p class="mb-0 text-sm font-weight-bold" style="color: #fb8840">
                  {{ currency_code }} {{ formatNumber(remaining) }}
                </p>
              </div>
            </div>
            <div class="progress-wrapper mt-3">
              <div class="progress">
                <div class="progress-bar bg-gradient-success" :style="{ width: progress + '%' }"></div>
              </div>
              <span class="text-xxs" style="opacity: 0.6">
                {{ $t("partial_payment.already_paid") }} : {{ currency_code }}
                {{ formatNumber(alreadyPaid) }} / {{ formatNumber(dueAmount) }}
              </span>
            </div>
          </div>

          <label class="form-label text-xs">{{ $t("partial_payment.amount") }}</label>
          <argon-input
            v-model="amount"
            type="number"
            size="default"
            :placeholder="$t('partial_payment.amount_placeholder')"
          />

          <!--
            Opérateur et identifiant de transaction sont deux informations
            distinctes : les séparer permet de retrouver un versement par
            opérateur, ce qu'un champ unique ne permettait pas.
            Côte à côte : deux champs courts, dont la pleine largeur était
            inutile et allongeait le formulaire.
          -->
          <div class="row g-2">
            <div class="col-md-6">
              <label class="form-label text-xs">{{ $t("partial_payment.operator") }}</label>
              <argon-input
                v-model="operator"
                type="text"
                size="default"
                :placeholder="$t('partial_payment.operator_placeholder')"
              />
            </div>
            <div class="col-md-6">
              <label class="form-label text-xs">{{ $t("partial_payment.transaction_id") }}</label>
              <argon-input
                v-model="transactionId"
                type="text"
                size="default"
                :placeholder="$t('partial_payment.transaction_id_placeholder')"
              />
            </div>
          </div>

          <label class="form-label text-xs">{{ $t("partial_payment.date") }}</label>
          <argon-input v-model="paymentDate" type="date" size="default" />

          <p class="text-xs mt-2 mb-0" style="color: #f5365c" v-if="error">{{ error }}</p>
        </div>

        <div class="modal-footer pb-4" style="border-top: none">
          <argon-button
            type="button"
            class="btn bg-transparent"
            style="width: 150px; height: 50px; padding-top: 15px; border: 1px solid rgba(255, 255, 255, 0.4)"
            @click="closeModal"
          >
            {{ $t("button.cancel") }}
          </argon-button>
          <argon-button
            color="success"
            variant="gradient"
            type="button"
            :loading="loading"
            style="width: 170px; height: 50px"
            @click="submit"
          >
            {{ $t("partial_payment.validate") }}
          </argon-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ArgonInput from "@/components/ArgonInput";
import ArgonButton from "@/components/ArgonButton";
import api_config from "@/config";
import { getinfoMembers, setPartialPayment } from "@/utils/services";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";

export default {
  name: "partial-payment-modal",
  components: { ArgonInput, ArgonButton },
  props: {
    isVisible: Boolean,
    student: Object,
    payment: Object,
  },
  emits: ["close", "payment-recorded"],
  data() {
    return {
      amount: "",
      operator: "",
      transactionId: "",
      /* Nom de l'utilisateur connecté, envoyé comme operator_user_id. */
      currentUserName: "",
      paymentDate: new Date().toISOString().slice(0, 10),
      loading: false,
      error: "",
      currency_code: api_config.CURRENCY_CODE,
    };
  },
  computed: {
    dueAmount() {
      return Number(this.payment?.amount ?? this.payment?.due_amount ?? 0);
    },
    /**
     * Total déjà versé sur cette tranche.
     *
     * On somme les `splits` — la même source que l'infobulle de la pastille
     * (getPendingTitle), qui détaille chaque versement. `paid_amount` seul
     * ne suivait pas : après un versement enregistré ici, l'infobulle
     * affichait le nouveau montant alors que la barre restait figée.
     *
     * Repli sur `paid_amount` quand l'API ne renvoie pas le détail.
     */
    alreadyPaid() {
      const splits = this.payment && this.payment.splits;
      if (Array.isArray(splits) && splits.length) {
        return splits.reduce((total, split) => total + Number(split.amount || 0), 0);
      }
      return Number((this.payment && this.payment.paid_amount) || 0);
    },
    remaining() {
      return Math.max(this.dueAmount - this.alreadyPaid, 0);
    },
    progress() {
      if (!this.dueAmount) return 0;
      return Math.min(Math.round((this.alreadyPaid / this.dueAmount) * 100), 100);
    },
  },
  watch: {
    isVisible(value) {
      if (value) {
        this.amount = "";
        this.operator = "";
        this.transactionId = "";
        this.error = "";
        this.paymentDate = new Date().toISOString().slice(0, 10);
        this.loadCurrentUser();
      }
    },
  },
  methods: {
    /**
     * Nom de l'utilisateur qui saisit le paiement.
     *
     * Envoyé comme `operator_user_id` : c'est la personne qui enregistre le
     * versement, pas une saisie du formulaire. Même source que la page
     * /account-information (getinfoMembers sur le username stocké).
     *
     * Un échec n'empêche pas l'enregistrement : le champ part vide plutôt que
     * de bloquer la saisie d'un paiement pour une information secondaire.
     */
    async loadCurrentUser() {
      try {
        const username = localStorage.getItem("username");
        if (!username) return;
        const userData = await getinfoMembers(username);
        if (!userData) return;
        this.currentUserName = [userData.first_name, userData.last_name]
          .filter(Boolean)
          .join(" ")
          .trim() || userData.username || "";
      } catch (error) {
        console.error("Failed to load current user", error);
      }
    },
    formatNumber(value) {
      const locale = this.$i18n.locale === "fr" ? "fr-FR" : "en-US";
      return Number(value || 0).toLocaleString(locale, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    closeModal() {
      this.$emit("close");
    },
    async submit() {
      const value = Number(this.amount);
      if (!value || value <= 0) {
        this.error = this.$t("partial_payment.error_amount");
        return;
      }
      if (this.dueAmount && value > this.remaining) {
        this.error = this.$t("partial_payment.error_too_much");
        return;
      }
      if (!this.operator) {
        this.error = this.$t("partial_payment.error_operator");
        return;
      }
      if (!this.transactionId) {
        this.error = this.$t("partial_payment.error_transaction_id");
        return;
      }

      try {
        this.loading = true;
        this.error = "";

        // Charge utile attendue par /api/set_partial_payment.
        const payload = {
          payment_id: this.payment?.id,
          amount_paid: value,
          operator_code: this.operator,
          operator_tx_id: this.transactionId,
          operator_user_id: this.currentUserName,
          processed_on: this.paymentDate,
        };

        const res = await setPartialPayment(payload);
        if (!res || (res.status !== 200 && res.status !== 201)) {
          this.error = this.$t("partial_payment.error_save");
          return;
        }

        /*
         * Mise à jour locale, émise APRÈS le succès de l'appel : la pastille
         * et la barre de progression ne doivent pas afficher un versement que
         * le serveur n'a pas enregistré.
         */
        this.$emit("payment-recorded", {
          student_id: this.student?.id,
          payment_id: this.payment?.id,
          amount: value,
          operator: this.operator,
          transaction_id: this.transactionId,
          operator_user_id: this.currentUserName,
          // Référence lisible, attendue par l'infobulle de la pastille et
          // l'historique des versements.
          reference: `${this.operator} — ${this.transactionId}`,
          paid_on: this.paymentDate,
        });
        toast.success(this.$t("partial_payment.success"), { autoClose: 3000 });
        this.closeModal();
      } catch (error) {
        console.error("Failed to record partial payment", error);
        this.error = this.$t("partial_payment.error_save");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.recap {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 14px;
}

.progress {
  height: 6px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  margin-bottom: 6px;
}
</style>
